import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarBlank, CalendarPlus, PaperPlaneTilt, CheckCircle, ArrowRight, ChatCircle,
} from '@phosphor-icons/react';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/58284b88-53d8-4c2d-a757-f70994c6780a/images/d755f809849fdfafefb30a1695048e9941928fe07683681719c2f4c41f8a15d8.png';
const WORKER_IMG = 'https://static.prod-images.emergentagent.com/jobs/58284b88-53d8-4c2d-a757-f70994c6780a/images/67ec4ca3365fcbc6c28ea46e14c0c792afead2a3c57a76a0cba89f8f72702d3d.png';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const BOOKED = new Set([4, 5, 12]);
const SELECTED = new Set([17, 18]);

const STEPS = [
  {
    num: '01',
    title: 'See real availability',
    body: 'Every worker and contractor profile shows a live calendar — green days are open, grey days are booked. Vacation mode, next-available date, all public. No guessing, no waiting on a reply to "are you free?".',
  },
  {
    num: '02',
    title: 'Request your dates',
    body: 'Tap the open days you need — up to 10 at once — and hit "Request dates". A pre-written booking message with your exact dates lands in the pro\u2019s inbox instantly.',
  },
  {
    num: '03',
    title: 'One-tap confirmation',
    body: 'The pro taps "Accept & block these dates" right in chat. The days lock on their calendar automatically, you get a confirmation, and both of you can add it to Google Calendar or download the .ics file.',
  },
];

/**
 * "Book by Calendar" landing promo — full booking-flow showcase with
 * photography, live calendar mock, and 3-step explanation.
 */
const BookingCalendarPromo = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-16 md:py-24 border-y-4 border-black bg-[#0F172A] overflow-hidden" data-testid="booking-calendar-promo">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 bg-[#2B4C6F] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-white rounded mb-4 shadow-[3px_3px_0px_#fff]">
            <CalendarBlank size={14} weight="fill" /> Booking Calendars · New
          </div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight mb-4">
            See who's free.
            <span className="block text-[#D95800]">Book them in two taps.</span>
          </h2>
          <p className="text-neutral-300 text-base md:text-lg">
            The old way: five messages back and forth just to find a date. The ConstructHelp way:
            open a pro's profile, tap the days you need, done. Booking a contractor now works like
            booking a table.
          </p>
        </div>

        {/* Visual row: photo + calendar mock + accept bubble */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-14">
          <div className="relative border-4 border-black shadow-[8px_8px_0px_#D95800] min-h-[300px]">
            <img
              src={HERO_IMG}
              alt="Contractor showing a homeowner his availability calendar on a phone"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-4 py-3">
              <p className="text-white text-sm font-bold flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                "We picked the dates together on the spot — no phone tag."
              </p>
            </div>
          </div>

          <div className="relative flex flex-col justify-center">
            <div className="bg-white border-4 border-black rounded-xl p-5 shadow-[8px_8px_0px_#2B4C6F] max-w-sm mx-auto w-full">
              <div className="flex items-center justify-between mb-3">
                <span className="font-black text-sm text-neutral-900 flex items-center gap-1.5">
                  <CalendarBlank size={16} weight="bold" className="text-[#2B4C6F]" /> July
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px] font-black uppercase">
                  <CheckCircle size={11} weight="fill" /> Available today
                </span>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAYS.map((d, i) => (
                  <div key={i} className="text-center text-[10px] font-bold text-neutral-400">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => {
                  let cls = 'bg-emerald-50 text-emerald-800 border-emerald-200';
                  if (BOOKED.has(d)) cls = 'bg-neutral-200 text-neutral-400 border-neutral-300 line-through';
                  if (SELECTED.has(d)) cls = 'bg-[#2B4C6F] text-white border-black shadow-[2px_2px_0px_#000]';
                  return (
                    <div key={d} className={`aspect-square flex items-center justify-center text-[11px] font-bold rounded border ${cls}`}>
                      {d}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 h-9 flex items-center justify-center gap-1.5 bg-[#2B4C6F] text-white text-xs font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000]">
                <PaperPlaneTilt size={14} weight="bold" /> Request 2 dates
              </div>
            </div>

            {/* Accept bubble */}
            <div className="bg-white border-4 border-black rounded-xl p-3 shadow-[6px_6px_0px_#000] max-w-[250px] mx-auto lg:absolute lg:-bottom-4 lg:right-4 mt-4 lg:mt-0 z-10">
              <div className="flex items-center gap-1.5 text-[11px] text-neutral-500 font-bold mb-1.5">
                <ChatCircle size={13} weight="fill" className="text-[#D95800]" /> Mike R. · Electrician
              </div>
              <div className="h-8 flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-[11px] font-bold rounded-lg border-2 border-black">
                <CheckCircle size={13} weight="bold" /> Accept & block these dates
              </div>
              <div className="mt-1.5 text-[10px] text-neutral-500 flex items-center gap-1">
                <CalendarPlus size={12} weight="bold" /> Added to Google Calendar ✓
              </div>
            </div>
          </div>
        </div>

        {/* 3 detailed steps + worker photo */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 items-stretch">
          {STEPS.map(({ num, title, body }) => (
            <div key={num} className="bg-white/5 border-2 border-white/15 p-6 hover:border-[#D95800] transition-colors">
              <div className="text-4xl font-black text-[#D95800]/40 mb-2">{num}</div>
              <h3 className="text-white font-black uppercase text-sm tracking-wide mb-2">{title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
          <div className="relative border-2 border-white/15 min-h-[220px] hidden lg:block">
            <img
              src={WORKER_IMG}
              alt="Worker accepting a booking request on his phone at a job site"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute bottom-2 left-2 right-2 bg-emerald-600 border-2 border-black px-2 py-1.5 text-[11px] font-bold text-white flex items-center gap-1.5">
              <CheckCircle size={13} weight="fill" /> Booked! Dates reserved for you.
            </div>
          </div>
        </div>

        {/* Dual CTA */}
        <div className="flex flex-col sm:flex-row gap-3 mt-10">
          <button
            onClick={() => navigate('/browse-worker-teams')}
            className="inline-flex items-center justify-center gap-2 h-13 px-6 py-3.5 bg-[#D95800] text-black font-black uppercase text-sm tracking-wide border-2 border-white shadow-[4px_4px_0px_#fff] hover:-translate-y-0.5 transition-transform"
            data-testid="booking-promo-cta"
          >
            Browse Available Pros <ArrowRight size={16} weight="bold" />
          </button>
          <button
            onClick={() => navigate('/my-availability')}
            className="inline-flex items-center justify-center gap-2 h-13 px-6 py-3.5 bg-transparent text-white font-black uppercase text-sm tracking-wide border-2 border-white shadow-[4px_4px_0px_#2B4C6F] hover:-translate-y-0.5 transition-transform"
            data-testid="booking-promo-pro-cta"
          >
            <CalendarBlank size={16} weight="bold" /> I'm a Pro — Set My Availability
          </button>
        </div>
      </div>
    </section>
  );
};

export default BookingCalendarPromo;
